import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'dark' | 'light' | 'system';

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

interface ThemeProviderState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  rehydrateFromApi: (apiTheme: Theme | null) => void;
}

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
  rehydrateFromApi: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

/**
 * Provides theme context and applies the selected theme to the document root.
 */
export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof localStorage === 'undefined') {
      return defaultTheme;
    }

    return (localStorage.getItem(storageKey) as Theme | null) ?? defaultTheme;
  });

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';

      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  /**
   * Rehydrate theme from API response, updating local state if it differs
   */
  const rehydrateFromApi = (apiTheme: Theme | null) => {
    if (apiTheme && apiTheme !== theme) {
      localStorage.setItem(storageKey, apiTheme);
      setTheme(apiTheme);
    }
  };

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      localStorage.setItem(storageKey, newTheme);
      setTheme(newTheme);
    },
    rehydrateFromApi,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

/**
 * Retrieves the current theme state and updater from context.
 */
export const useTheme = () => {
  return useContext(ThemeProviderContext);
};
